import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export const STORAGE_KEY = 'feed-optimizer-cleanup-lock';
const MIN_HOLD_MS = 1_500;
const MAX_LOCK_MS = 30_000;
const POLL_MS = 750;

// Display estimate for the countdown UI only — NOT enforced as a wait.
// Actual unlock is signal-driven by polling products + optimization_jobs + score_calculation_jobs.
export function computeMinHoldMs(productCount: number): number {
  return Math.min(8_000, 2_000 + Math.round(productCount * 0.1));
}

interface PersistedLock {
  feedId: string;
  userId: string;
  startedAt: number;
  productCount: number;
}

function safeStorageRead(): PersistedLock | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedLock;
    if (!parsed?.feedId || !parsed?.userId || !parsed?.startedAt) return null;
    return parsed;
  } catch {
    return null;
  }
}

function safeStorageWrite(lock: PersistedLock): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(lock));
  } catch {
    // privacy mode / quota — degrade to in-memory only
  }
}

function safeStorageClear(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

interface CleanupLockState {
  locked: boolean;
  secondsRemaining: number;
}

interface Opts {
  triggered: boolean;
  feedProductCount: number;
  userId: string | null;
}

export function useCancelCleanupLock(
  feedId: string | null,
  opts: Opts,
): CleanupLockState {
  const [startedAt, setStartedAt] = useState<number | null>(() => {
    const persisted = safeStorageRead();
    if (
      persisted &&
      feedId === persisted.feedId &&
      opts.userId === persisted.userId &&
      Date.now() - persisted.startedAt < MAX_LOCK_MS
    ) {
      return persisted.startedAt;
    }
    if (persisted) safeStorageClear();
    return null;
  });

  useEffect(() => {
    if (opts.triggered && startedAt === null && feedId && opts.userId) {
      const now = Date.now();
      setStartedAt(now);
      safeStorageWrite({
        feedId,
        userId: opts.userId,
        startedAt: now,
        productCount: opts.feedProductCount,
      });
    }
  }, [opts.triggered, startedAt, feedId, opts.userId, opts.feedProductCount]);

  // Cross-tab sync via storage event.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      if (e.newValue === null) {
        setStartedAt(null);
        return;
      }
      try {
        const next = JSON.parse(e.newValue) as PersistedLock;
        if (next.feedId === feedId && next.userId === opts.userId && startedAt === null) {
          setStartedAt(next.startedAt);
        }
      } catch {
        // ignore malformed
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [feedId, opts.userId, startedAt]);

  // Tick for countdown re-render
  const [, setTick] = useState(0);
  useEffect(() => {
    if (startedAt === null) return;
    const i = setInterval(() => setTick((t) => t + 1), 250);
    return () => clearInterval(i);
  }, [startedAt]);

  // Poll cleanup signals: products + optimization_jobs + score_calculation_jobs.
  // In-flight workers that already dequeued before cancel_async_job deleted this job's messages will write back into
  // products with optimization_status='completed' (no-op for the signal) or fail
  // and leave 'pending', which keeps this signal honest.
  const { data: cleanupDone } = useQuery({
    queryKey: ['cancel-cleanup-lock', feedId],
    queryFn: async () => {
      if (!feedId) return true;
      const [pendingResult, jobsResult, scoresResult] = await Promise.all([
        supabase
          .from('products')
          .select('id', { count: 'exact', head: true })
          .eq('feed_id', feedId)
          .in('optimization_status', ['pending', 'processing']),
        supabase
          .from('optimization_jobs')
          .select('id')
          .eq('feed_id', feedId)
          .in('status', ['queued', 'processing'])
          .limit(1),
        supabase
          .from('score_calculation_jobs')
          .select('id')
          .eq('feed_id', feedId)
          .in('status', ['queued', 'processing'])
          .limit(1),
      ]);
      const productsClean = (pendingResult.count ?? 0) === 0;
      const jobsClean = !jobsResult.data || jobsResult.data.length === 0;
      const scoresClean = !scoresResult.data || scoresResult.data.length === 0;
      return productsClean && jobsClean && scoresClean;
    },
    enabled: !!feedId && startedAt !== null,
    refetchInterval: (query) => (query.state.data ? false : POLL_MS),
  });

  if (startedAt === null) {
    return { locked: false, secondsRemaining: 0 };
  }

  const elapsed = Date.now() - startedAt;
  const minHoldRemaining = Math.max(0, MIN_HOLD_MS - elapsed);
  const hardCapHit = elapsed >= MAX_LOCK_MS;
  const stillCleaning = !cleanupDone;
  const shouldStayLocked = !hardCapHit && (stillCleaning || minHoldRemaining > 0);

  if (!shouldStayLocked && startedAt !== null) {
    safeStorageClear();
    queueMicrotask(() => setStartedAt(null));
  }

  const estimateMs = computeMinHoldMs(opts.feedProductCount);
  const secondsRemaining = shouldStayLocked
    ? Math.max(
        1,
        Math.ceil(
          (stillCleaning
            ? Math.max(minHoldRemaining, estimateMs - elapsed, 1000)
            : minHoldRemaining) / 1000,
        ),
      )
    : 0;

  return { locked: shouldStayLocked, secondsRemaining };
}
